import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    Container,
    Text,
    Title,
    Alert,
    Center,
    Loader,
    Box,
    Group,
    Button
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import API_BASE_URL from "../config";
import SeatGrid from "./SeatGrid";

function Seats() {
    const { movieId } = useParams();
    const navigate = useNavigate();
    const [seats, setSeats] = useState([]);
    const [selectedSeats, setSelectedSeats] = useState([]); // 다중 선택된 좌석들
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSeats = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/seats/movie/${movieId}`);
                setSeats(response.data);
            } catch (err) {
                setError("Failed to fetch seats: " + (err.response?.data?.message || "Unknown error"));
            } finally {
                setLoading(false);
            }
        };
        fetchSeats();
    }, [movieId]);

    const selectSeat = async (seatId) => {
        try {
            await axios.post(`${API_BASE_URL}/api/seats/${seatId}/select`);
            
            // 좌석 선택/해제 토글
            setSelectedSeats(prev => {
                if (prev.includes(seatId)) {
                    // 이미 선택된 좌석이면 해제
                    return prev.filter(id => id !== seatId);
                } else {
                    // 새로 선택
                    return [...prev, seatId];
                }
            });
            
            // UI 업데이트
            setSeats((prevSeats) =>
                prevSeats.map((seat) =>
                    seat.id === seatId ? { ...seat, selected: !seat.selected } : seat
                )
            );
        } catch (err) {
            alert("Failed to select seat: " + (err.response?.data?.message || "Unknown error"));
        }
    };

    const proceedToPayment = () => {
        if (selectedSeats.length === 0) {
            alert("좌석을 선택해주세요.");
            return;
        }

        const username = localStorage.getItem('username');
        if (!username) {
            alert("Please login again.");
            return;
        }

        // 선택된 좌석들 정보 수집
        const selectedSeatDetails = seats.filter(seat => selectedSeats.includes(seat.id));
        const totalAmount = selectedSeatDetails.length * 15000; // 좌석당 15,000원

        navigate('/payment', {
            state: {
                selectedSeats: selectedSeats,
                seatDetails: selectedSeatDetails,
                movieTitle: '영화 제목', // 실제로는 영화 정보를 가져와야 함
                totalAmount: totalAmount,
                username: username
            }
        });
    };

    if (loading) return <Center h="50vh"><Loader color="white" /></Center>;

    return (
        <Container size="lg" py="xl">
            <Center mb={40}>
                <Box>
                    <Title order={2} align="center" style={{ color: 'white' }}>Select Your Seats</Title>
                    <Text align="center" color="dimmed" size="sm" mt={5}>Movie Screen</Text>
                    <Box
                        sx={{
                            height: 4,
                            width: 300,
                            background: 'linear-gradient(90deg, transparent, white, transparent)',
                            marginTop: 10,
                            borderRadius: 2
                        }}
                    />
                </Box>
            </Center>

            {error && (
                <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" mb="xl">
                    {error}
                </Alert>
            )}

            <SeatGrid
                seats={seats}
                onSeatClick={(seat) => {
                    if (seat.reserved) return;
                    selectSeat(seat.id);
                }}
            />

            {/* 선택된 좌석 정보 및 결제 버튼 */}
            {selectedSeats.length > 0 && (
                <Box mt="xl" p="md" style={{ backgroundColor: '#2a2a2a', borderRadius: 8, border: '1px solid #444' }}>
                    <Text size="lg" weight={500} mb="sm" style={{ color: 'white' }}>
                        선택된 좌석 ({selectedSeats.length}개)
                    </Text>
                    <Group spacing="xs" mb="md">
                        {seats.filter(seat => selectedSeats.includes(seat.id)).map(seat => (
                            <Text key={seat.id} size="sm" style={{ 
                                color: '#0071e3', 
                                backgroundColor: '#1a1a1a', 
                                padding: '4px 8px', 
                                borderRadius: 4,
                                border: '1px solid #0071e3'
                            }}>
                                {seat.seatNumber}
                            </Text>
                        ))}
                    </Group>
                    <Group position="apart" align="center">
                        <Text size="lg" weight={500} style={{ color: 'white' }}>
                            총 금액: {(selectedSeats.length * 15000).toLocaleString()}원
                        </Text>
                        <Button
                            onClick={proceedToPayment}
                            size="lg"
                            style={{ backgroundColor: '#0071e3' }}
                        >
                            결제하기
                        </Button>
                    </Group>
                </Box>
            )}

            <Group position="center" mt={40} spacing={40}>
                <Group spacing={10}>
                    <Box w={20} h={20} style={{ backgroundColor: '#1c1c1e', border: '1px solid #333', borderRadius: 4 }} />
                    <Text size="sm" color="dimmed">Available</Text>
                </Group>
                <Group spacing={10}>
                    <Box w={20} h={20} style={{ backgroundColor: '#0071e3', borderRadius: 4 }} />
                    <Text size="sm" color="dimmed">Selected</Text>
                </Group>
                <Group spacing={10}>
                    <Box w={20} h={20} style={{ backgroundColor: '#111', border: '1px solid #222', borderRadius: 4 }} />
                    <Text size="sm" color="dimmed">Occupied</Text>
                </Group>
            </Group>
        </Container>
    );
}

export default Seats;
